import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Calendar,
  FileText,
  AlertTriangle,
  ClipboardList,
  Pill,
  Heart,
  Users,
  Bell,
  Activity,
  RefreshCw,
  PauseCircle,
  PlayCircle,
} from 'lucide-react';
import { patientsApi } from '@/api/endpoints/patients.api';
import type { TimelineEvent, TimelineEventType } from '@/types/patient.types';

interface PatientTimelineProps {
  patientId: string;
}

const EVENT_ICONS: Record<TimelineEventType, React.ElementType> = {
  CARE_PLAN: ClipboardList,
  CARE_PLAN_STATUS_CHANGE: Activity,
  RISK_ASSESSMENT: AlertTriangle,
  QUESTIONNAIRE_RESPONSE: FileText,
  PROBLEM_ADDED: Heart,
  PROBLEM_RESOLVED: Heart,
  MEDICATION_STARTED: Pill,
  MEDICATION_SUSPENDED: PauseCircle,
  MEDICATION_REACTIVATED: PlayCircle,
  MEDICATION_STOPPED: Pill,
  CARE_LINE_ENROLLMENT: Users,
  CARE_LINE_DISCHARGE: Users,
  ALERT: Bell,
};

const EVENT_COLORS: Record<TimelineEventType, string> = {
  CARE_PLAN: 'bg-blue-500',
  CARE_PLAN_STATUS_CHANGE: 'bg-cyan-500',
  RISK_ASSESSMENT: 'bg-orange-500',
  QUESTIONNAIRE_RESPONSE: 'bg-purple-500',
  PROBLEM_ADDED: 'bg-red-500',
  PROBLEM_RESOLVED: 'bg-green-500',
  MEDICATION_STARTED: 'bg-teal-500',
  MEDICATION_SUSPENDED: 'bg-amber-500',
  MEDICATION_REACTIVATED: 'bg-emerald-500',
  MEDICATION_STOPPED: 'bg-gray-500',
  CARE_LINE_ENROLLMENT: 'bg-indigo-500',
  CARE_LINE_DISCHARGE: 'bg-slate-500',
  ALERT: 'bg-yellow-500',
};

export function PatientTimeline({ patientId }: PatientTimelineProps) {
  const [dateFilter, setDateFilter] = useState<{ startDate?: string; endDate?: string }>({});

  // Query: Timeline events
  const {
    data: events = [],
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['patient', patientId, 'timeline', dateFilter],
    queryFn: () => patientsApi.getTimeline(patientId, dateFilter),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Carregando linha do tempo...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-800">Erro ao carregar linha do tempo. Tente novamente.</p>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Calendar className="h-12 w-12 text-muted-foreground" />
        <h3 className="mt-4 text-lg font-medium">Nenhum evento registrado</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          A linha do tempo do paciente aparecerá aqui conforme eventos forem registrados.
        </p>
      </div>
    );
  }

  // Group events by date
  const groupedEvents = events.reduce((groups, event) => {
    const date = format(new Date(event.eventDate), 'yyyy-MM-dd');
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(event);
    return groups;
  }, {} as Record<string, TimelineEvent[]>);

  return (
    <div className="space-y-6">
      {/* Date filters */}
      <div className="flex gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">De:</label>
          <input
            type="date"
            className="rounded-md border px-3 py-1 text-sm"
            value={dateFilter.startDate || ''}
            onChange={(e) => setDateFilter((f) => ({ ...f, startDate: e.target.value || undefined }))}
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Até:</label>
          <input
            type="date"
            className="rounded-md border px-3 py-1 text-sm"
            value={dateFilter.endDate || ''}
            onChange={(e) => setDateFilter((f) => ({ ...f, endDate: e.target.value || undefined }))}
          />
        </div>
        {(dateFilter.startDate || dateFilter.endDate) && (
          <button
            className="text-sm text-primary hover:underline"
            onClick={() => setDateFilter({})}
          >
            Limpar filtros
          </button>
        )}
      </div>

      {/* Timeline */}
      <div className="relative">
        {Object.entries(groupedEvents).map(([date, dayEvents]) => (
          <div key={date} className="mb-8">
            {/* Date header */}
            <div className="sticky top-0 z-10 mb-4 bg-background py-2">
              <h3 className="text-sm font-semibold text-muted-foreground">
                {format(new Date(date), "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </h3>
            </div>

            {/* Events for this date */}
            <div className="relative ml-4 space-y-4 border-l-2 border-muted pl-6">
              {dayEvents.map((event) => {
                const Icon = EVENT_ICONS[event.type] || FileText;
                const colorClass = EVENT_COLORS[event.type] || 'bg-gray-500';

                return (
                  <div key={event.id} className="relative">
                    {/* Event marker */}
                    <div
                      className={`absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full ${colorClass}`}
                    >
                      <Icon className="h-3 w-3 text-white" />
                    </div>

                    {/* Event content */}
                    <div className="rounded-lg border bg-card p-4 shadow-sm">
                      <div className="flex items-start justify-between">
                        <div>
                          <span className="inline-block rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                            {event.typeLabel}
                          </span>
                          <h4 className="mt-1 font-medium">{event.title}</h4>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {event.description}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(event.eventDate), 'HH:mm', { locale: ptBR })}
                        </span>
                      </div>

                      {event.createdByName && (
                        <p className="mt-2 text-xs text-muted-foreground">
                          Por: {event.createdByName}
                        </p>
                      )}

                      {event.metadata && Object.keys(event.metadata).length > 0 && (
                        <div className="mt-2 rounded bg-muted/50 p-2">
                          <p className="text-xs font-medium text-muted-foreground">Detalhes:</p>
                          <dl className="mt-1 grid gap-1 text-xs">
                            {Object.entries(event.metadata).map(([key, value]) => (
                              <div key={key} className="flex gap-2">
                                <dt className="font-medium capitalize">{key.replace(/_/g, ' ')}:</dt>
                                <dd className="text-muted-foreground">{String(value)}</dd>
                              </div>
                            ))}
                          </dl>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
